import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { useLocation, useRouteMatch } from 'react-router-dom'
import { useNavigation } from '@/hooks/useNavigation'
import { useCallback, useEffect, useMemo } from 'react'
import {
  APPOINTMENT_STATES_ENUM,
  AppointmentRoutine,
  UseMonitoringProps,
} from '../types'
import { useModalContext } from '@/components/Modal/contexts/ModalContext'
import { useCallMonitoringStore } from '@/modules/VideoCall/store/call-monitoring-store'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import { useRealtimeAttendanceContext } from '@/modules/RealtimeAttendance/contexts/RealtimeAttendanceContext'
import {
  IAttendanceConfig,
  IHandleAttendanceStartWithToken,
} from '@/modules/RealtimeAttendance/types'
import { verifyTokenStatus } from '@/modules/RealtimeAttendance/services'
import { toast } from '@conexasaude/conexa-hero'
import { callAppointmentMonitoring } from '@/modules/VideoCall/video-call-services'

export const useMonitoringImmediate = ({
  realtimeAppointments,
}: UseMonitoringProps) => {
  const { clinic, user } = useAuthContext()
  const location = useLocation()
  const navigate = useNavigation()
  const { showModal } = useModalContext()
  const { realtimeAttendance } = useRealtimeAttendanceContext()
  const { withoutInteractionAttendance, embed } = useThemeGeneratorContext()
  const embedOrEmbedFull = embed?.value || embed?.full
  const waitingRoomImmediateMatch = useRouteMatch([
    '/atendimento-imediato/waiting',
  ])

  const isOnVideoCallPage = location.pathname.includes('chamada')

  const handleAttendanceStartWithToken = useCallback(
    async (params: IHandleAttendanceStartWithToken): Promise<void> => {
      const { attendanceId, modalToRender, modalTitle } = params

      try {
        const tokenIsValidated = await verifyTokenStatus(Number(attendanceId))
        if (tokenIsValidated) {
          showModal(modalToRender, {
            title: modalTitle,
            lockable: true,
            persistent: true,
          })
        } else {
          showModal('CALL_STARTED_WITH_TOKEN', {
            title: `Informe o token de segurança ${
              clinic?.friendlyName || user?.clinic?.name
            }`,
            persistent: true,
          })
        }
      } catch (err) {
        navigate('/')
        toast({
          message: err?.response?.data?.msg,
          variant: 'error',
          autoClose: false,
          position: 'top-right',
        })
      }
    },
    [clinic?.friendlyName, navigate, showModal, user?.clinic?.name],
  )

  const immediateRoutine: AppointmentRoutine = useMemo(() => {
    return {
      [APPOINTMENT_STATES_ENUM.WAITING_ATTENDANCE]() {
        if (isOnVideoCallPage) return

        navigate('/atendimento-imediato/waiting?o=ep')
      },
      [APPOINTMENT_STATES_ENUM.ATTENDANCE_STARTED]() {
        if (isOnVideoCallPage) return

        navigate('/atendimento-imediato/waiting?o=ep')
      },
      [APPOINTMENT_STATES_ENUM.CALL_STARTED](props) {
        if (isOnVideoCallPage) return

        callAppointmentMonitoring(props?.appointmentId).then(response => {
          if (response?.status === 'ENDED') return

          useCallMonitoringStore.setState({
            call: response,
            appointmentId: props?.appointmentId,
          })

          if (withoutInteractionAttendance?.value) {
            navigate(`/chamada?a=${props?.appointmentId}`)
            return
          }

          if (clinic?.flag?.LiberarChamadaPorTokenAutorizacaoExterno) {
            const attendanceConfig: IAttendanceConfig = {
              attendanceId: props?.appointmentId,
              modalToRender: 'CALL_STARTED',
              modalTitle: 'Tudo pronto para sua consulta',
            }
            handleAttendanceStartWithToken(attendanceConfig)
          } else {
            setTimeout(() => {
              showModal('CALL_STARTED', {
                title: 'Tudo pronto para sua consulta',
                lockable: true,
                persistent: true,
              })
            }, 500)
          }
        })
      },
      [APPOINTMENT_STATES_ENUM.CALL_RESTARTED](props) {
        if (isOnVideoCallPage) return

        callAppointmentMonitoring(props?.appointmentId).then(response => {
          if (response?.status === 'ENDED') return

          useCallMonitoringStore.setState({
            call: response,
            appointmentId: props?.appointmentId,
          })

          useCallMonitoringStore.setState({
            appointmentId: props?.appointmentId,
          })

          if (clinic?.flag?.LiberarChamadaPorTokenAutorizacaoExterno) {
            const attendanceConfig: IAttendanceConfig = {
              attendanceId: props?.appointmentId,
              modalToRender: 'CALL_RESTARTED',
              modalTitle: 'Consulta Reiniciada',
            }
            handleAttendanceStartWithToken(attendanceConfig)
          } else {
            setTimeout(() => {
              showModal('CALL_RESTARTED', {
                title: 'Consulta Reiniciada',
                persistent: true,
              })
            }, 500)
          }
        })
      },
      [APPOINTMENT_STATES_ENUM.CALL_ENDED](props) {
        const isCurrentAppointment =
          props?.appointmentId ===
          useCallMonitoringStore.getState().appointmentId

        if (waitingRoomImmediateMatch?.isExact && isCurrentAppointment) {
          showModal('CANCELLED_BY_MEDIC', {
            title: 'Consulta cancelada',
            lockable: true,
            persistent: true,
            width: 512,
          })
        }

        if (!isOnVideoCallPage || !isCurrentAppointment) return

        useCallMonitoringStore.setState({
          appointmentId: null,
          call: null,
        })

        if (withoutInteractionAttendance?.value) {
          clinic?.linkRedirectAfterAttendance
            ? (window.location.href = clinic?.linkRedirectAfterAttendance)
            : navigate('/')
          return
        }

        if (clinic?.flag?.DesabilitarNpsTelemedicina) {
          embedOrEmbedFull ? navigate('/meus-atendimentos') : navigate('/')
          return
        }

        navigate(`/avaliacao/${props?.appointmentId}`, {
          consultationType: 'immediate',
        })
      },
    }
  }, [
    navigate,
    isOnVideoCallPage,
    showModal,
    handleAttendanceStartWithToken,
    embedOrEmbedFull,
    clinic?.flag?.LiberarChamadaPorTokenAutorizacaoExterno,
    clinic?.flag?.DesabilitarNpsTelemedicina,
    clinic?.linkRedirectAfterAttendance,
    waitingRoomImmediateMatch?.isExact,
    withoutInteractionAttendance?.value,
  ])

  useEffect(() => {
    if (
      realtimeAttendance?.call ||
      realtimeAttendance?.immediate ||
      !waitingRoomImmediateMatch
    )
      return

    if (realtimeAppointments) {
      const filteredImmediate = realtimeAppointments?.filter(
        status => status.modoAtendimento === 'IMEDIATO',
      )

      filteredImmediate?.forEach(appointment => {
        if (immediateRoutine[appointment.status]) {
          immediateRoutine[appointment.status]({
            appointmentId: appointment.idAtendimento,
          })
        }
      })
    }
  }, [
    immediateRoutine,
    realtimeAppointments,
    realtimeAttendance?.call,
    realtimeAttendance?.immediate,
  ])
}
